import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams, Link } from "react-router-dom";
import PageLayout, {
  PageSection,
} from "../../components/navigation/PageLayout";
import CalculationSummary from "../tronc-distribution/components/CalculationSummary";
import EmployeeDistributionList from "../tronc-distribution/components/EmployeeDistributionList";
import Icon from "../../components/AppIcon";
import { useEmployees } from "../../hooks/useEmployees";
import { useTroncRecords } from "../../hooks/useTroncRecords";
import {
  useDistributionRules,
  useDistributionRuleMutations,
} from "../../hooks/useDistributionRules";
import { useJobRoles } from "../../hooks/useJobRoles";
import { useLocationContext } from "../../contexts/LocationContext";
import { useTenantContext } from "../../contexts/TenantContext";
import { eligibilityCriteriaApi } from "../../services/api";
import { clockRecordApi } from "../../services/api/clockRecords";
import {
  calculateForEmployees,
  getEligibilitySummary,
  toEligibilityEmployee,
  buildJobRolesMap,
  EligibilityReasonLabels,
} from "../../utils/eligibilityCalculator";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getDistributionTypeInfo = (type) => {
  // Matches backend enum: Equal=0, RoleScore=1, ByDepartment=2, WeightedHours=3
  const typeMap = {
    Equal: {
      name: "Equal Split",
      icon: "Users",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    RoleScore: {
      name: "By Job Role",
      icon: "PieChart",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    ByDepartment: {
      name: "By Department",
      icon: "Building2",
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
    },
    WeightedHours: {
      name: "Weighted Hours",
      icon: "Clock",
      color: "text-cyan-600",
      bgColor: "bg-cyan-100",
    },
    // Legacy string aliases
    ByJobRole: {
      name: "By Job Role",
      icon: "PieChart",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    ByJobRoleScore: {
      name: "By Job Role",
      icon: "PieChart",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    ByHoursAndJobRole: {
      name: "Weighted Hours",
      icon: "Clock",
      color: "text-cyan-600",
      bgColor: "bg-cyan-100",
    },
    // Numeric keys matching backend enum
    0: {
      name: "Equal Split",
      icon: "Users",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    1: {
      name: "By Job Role",
      icon: "PieChart",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    2: {
      name: "By Department",
      icon: "Building2",
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
    },
    3: {
      name: "Weighted Hours",
      icon: "Clock",
      color: "text-cyan-600",
      bgColor: "bg-cyan-100",
    },
  };
  return (
    typeMap[type] || {
      name: "Custom",
      icon: "Settings",
      color: "text-gray-600",
      bgColor: "bg-gray-100",
    }
  );
};

const DistributionLab = () => {
  const { currentLocation } = useLocationContext();
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } =
    useTenantContext();
  const [searchParams] = useSearchParams();
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [manualAmount, setManualAmount] = useState("");
  const [employeeAmounts, setEmployeeAmounts] = useState({});
  const [calculatedDistribution, setCalculatedDistribution] = useState([]);
  const [notification, setNotification] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Date selector (defaults to today)
  const [labDate, setLabDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );

  // Eligibility criteria state
  const [eligibilityCriteria, setEligibilityCriteria] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilityResults, setEligibilityResults] = useState([]);

  // Clock records for the selected date (actual hours worked)
  const [clockRecords, setClockRecords] = useState([]);
  const [clockRecordsLoading, setClockRecordsLoading] = useState(false);

  const shouldFetchData = !isGlobalAdmin || selectedTenant;

  const { employees: apiEmployees, loading: employeesLoading } = useEmployees({
    autoFetch: shouldFetchData,
    activeOnly: false,
    troncEligibleOnly: false,
  });
  const { rules, loading: rulesLoading } = useDistributionRules({
    activeOnly: false,
    autoFetch: shouldFetchData,
  });
  const { previewDistribution, loading: previewLoading } =
    useDistributionRuleMutations();
  const { records: troncRecords, loading: troncLoading } = useTroncRecords({
    autoFetch: shouldFetchData,
  });
  const { jobRoles, loading: jobRolesLoading } = useJobRoles({
    autoFetch: shouldFetchData,
    activeOnly: false,
  });

  const loading =
    employeesLoading || rulesLoading || troncLoading || jobRolesLoading;

  // Build job roles map for eligibility calculator
  const jobRolesMap = useMemo(
    () => buildJobRolesMap(jobRoles || []),
    [jobRoles],
  );

  // Job role lookup by ID for display
  const jobRoleLookup = useMemo(() => {
    const map = {};
    (jobRoles || []).forEach((r) => {
      map[r.id] = r;
    });
    return map;
  }, [jobRoles]);

  // Fetch eligibility criteria for the selected date
  const fetchEligibilityCriteria = useCallback(async () => {
    if (!shouldFetchData) return;
    setEligibilityLoading(true);
    try {
      const res = await eligibilityCriteriaApi.getForDate(labDate);
      if (res.success) {
        setEligibilityCriteria(res.data || null);
      } else {
        setEligibilityCriteria(null);
      }
    } catch (err) {
      console.debug("Failed to load eligibility criteria for date:", err);
      setEligibilityCriteria(null);
    } finally {
      setEligibilityLoading(false);
    }
  }, [labDate, shouldFetchData]);

  useEffect(() => {
    fetchEligibilityCriteria();
  }, [fetchEligibilityCriteria]);

  // Ensure active eligibility criteria exists
  const handleEnsureEligibility = async () => {
    try {
      setEligibilityLoading(true);
      const res = await eligibilityCriteriaApi.ensureActive();
      if (res.success && res.data) {
        setEligibilityCriteria(res.data);
        showNotification("Default eligibility criteria created and activated.");
      } else {
        showNotification(
          res.message || "Failed to create eligibility criteria.",
          "error",
        );
      }
    } catch (err) {
      showNotification(
        err.message || "Failed to create eligibility criteria.",
        "error",
      );
    } finally {
      setEligibilityLoading(false);
    }
  };

  // Fetch clock records for the selected date
  const fetchClockRecords = useCallback(async () => {
    if (!shouldFetchData || !labDate) return;
    setClockRecordsLoading(true);
    try {
      const res = await clockRecordApi.getAll({
        fromDate: labDate,
        toDate: labDate,
      });
      const data = res?.data ?? res?.Data ?? [];
      setClockRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      console.debug("Failed to load clock records for date:", err);
      setClockRecords([]);
    } finally {
      setClockRecordsLoading(false);
    }
  }, [labDate, shouldFetchData]);

  useEffect(() => {
    fetchClockRecords();
  }, [fetchClockRecords]);

  // Build a map of employeeId -> total hours worked from clock records for the selected date
  const clockHoursMap = useMemo(() => {
    const map = {};
    (clockRecords || []).forEach((cr) => {
      const empId = cr.employeeId;
      if (!empId) return;
      const hours = cr.hoursWorked ?? 0;
      map[empId] = (map[empId] || 0) + hours;
    });
    return map;
  }, [clockRecords]);

  // Build eligibility rules from criteria for the calculator
  const eligibilityRules = useMemo(() => {
    if (!eligibilityCriteria) {
      return {
        requireEmployeeIsActive: true,
        requireEmployeeIsEligible: true,
        requireRoleIsEligible: true,
        requireRoleIsActive: true,
        excludeEmployeesInProbation: false,
        excludeManagementRoles: false,
        requireMinimumServiceDays: false,
        minimumServiceDays: 0,
        requireMinimumHoursInPeriod: false,
        minimumHoursInPeriod: 0,
        requireEmploymentStarted: true,
        excludeTerminatedEmployees: true,
      };
    }
    return {
      requireEmployeeIsActive:
        eligibilityCriteria.requireEmployeeIsActive ?? true,
      requireEmployeeIsEligible:
        eligibilityCriteria.requireEmployeeIsEligible ?? true,
      requireRoleIsEligible: eligibilityCriteria.requireRoleIsEligible ?? true,
      requireRoleIsActive: eligibilityCriteria.requireRoleIsActive ?? true,
      excludeEmployeesInProbation:
        eligibilityCriteria.excludeEmployeesInProbation ?? false,
      excludeManagementRoles:
        eligibilityCriteria.excludeManagementRoles ?? false,
      requireMinimumServiceDays:
        eligibilityCriteria.requireMinimumServiceDays ?? false,
      minimumServiceDays: eligibilityCriteria.minimumServiceDays ?? 0,
      requireMinimumHoursInPeriod:
        eligibilityCriteria.requireMinimumHoursInPeriod ?? false,
      minimumHoursInPeriod: eligibilityCriteria.minimumHoursInPeriod ?? 0,
      requireEmploymentStarted:
        eligibilityCriteria.requireEmploymentStarted ?? true,
      excludeTerminatedEmployees:
        eligibilityCriteria.excludeTerminatedEmployees ?? true,
    };
  }, [eligibilityCriteria]);

  // Run eligibility calculator on all employees
  const allEligibilityResults = useMemo(() => {
    if (!apiEmployees || apiEmployees.length === 0) return [];
    const eligEmployees = apiEmployees.map(toEligibilityEmployee);
    return calculateForEmployees(eligEmployees, jobRolesMap, {
      date: labDate,
      rules: eligibilityRules,
      clockRecords: [],
    });
  }, [apiEmployees, jobRolesMap, labDate, eligibilityRules]);

  const eligibilitySummary = useMemo(
    () => getEligibilitySummary(allEligibilityResults),
    [allEligibilityResults],
  );

  // Filter to only eligible employee IDs
  const eligibleEmployeeIds = useMemo(() => {
    return new Set(
      allEligibilityResults
        .filter((r) => r.isEligible)
        .map((r) => r.employeeId),
    );
  }, [allEligibilityResults]);

  const last30DaysRecords = useMemo(() => {
    if (!troncRecords) return [];
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return troncRecords
      .filter((r) => {
        const recordDate = new Date(r.periodStart);
        const matchesLocation =
          !currentLocation || r.locationId === currentLocation.id;
        return recordDate >= thirtyDaysAgo && matchesLocation;
      })
      .sort((a, b) => new Date(b.periodStart) - new Date(a.periodStart))
      .slice(0, 30);
  }, [troncRecords, currentLocation?.id]);

  const selectedRecord = useMemo(() => {
    if (!selectedRecordId || !last30DaysRecords) return null;
    return last30DaysRecords.find((r) => r.id === selectedRecordId);
  }, [selectedRecordId, last30DaysRecords]);

  const testAmount = selectedRecord?.amount || parseFloat(manualAmount) || 0;

  const locationFilteredEmployees = currentLocation
    ? (apiEmployees || []).filter(
        (emp) => emp.locationId === currentLocation.id,
      )
    : apiEmployees || [];

  // Apply eligibility criteria to filter employees
  const eligibleEmployees = useMemo(() => {
    return locationFilteredEmployees
      .filter((emp) => eligibleEmployeeIds.has(emp.id))
      .map((emp) => {
        const role = jobRoleLookup[emp.primaryJobRoleId || emp.jobRoleId];
        // Use clock record hours for the selected date if available, otherwise fall back to contracted hours
        const actualHours = clockHoursMap[emp.id];
        // For display: if we have clock records, show actual daily hours; otherwise show weekly hours divided by 5 as estimate
        const displayHours =
          actualHours != null
            ? actualHours
            : (emp.contractedHoursPerWeek || 40) / 5;
        return {
          id: emp.id,
          name:
            emp.fullName ||
            `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
          role:
            role?.name ||
            emp.primaryJobRoleName ||
            emp.jobTitle ||
            "Unassigned",
          jobRoleId: emp.primaryJobRoleId || emp.jobRoleId,
          primaryJobRoleId: emp.primaryJobRoleId || emp.jobRoleId,
          department: role?.department || emp.department,
          avatar:
            emp.photoUrl ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.firstName || "")}+${encodeURIComponent(emp.lastName || "")}&background=1a365d&color=fff`,
          hoursWorked: displayHours,
          hasClockRecord: actualHours != null,
          troncScore: emp.troncScore,
          calculatedAmount: 0,
        };
      });
  }, [
    locationFilteredEmployees,
    eligibleEmployeeIds,
    jobRoleLookup,
    clockHoursMap,
  ]);

  const selectedRule = useMemo(() => {
    if (!selectedRuleId || !rules) return null;
    return rules.find((r) => r.id === selectedRuleId);
  }, [selectedRuleId, rules]);

  useEffect(() => {
    setEmployeeAmounts({});
    setCalculatedDistribution([]);
    setSelectedRecordId(null);
  }, [currentLocation?.id]);

  // Auto-select rule from URL query param
  useEffect(() => {
    const ruleIdParam = searchParams.get("ruleId");
    if (ruleIdParam && rules && rules.length > 0) {
      const matchingRule = rules.find((r) => r.id === ruleIdParam);
      if (matchingRule) {
        setSelectedRuleId(ruleIdParam);
      }
    }
  }, [searchParams, rules]);

  // Auto-calculate when rule and amount are available
  useEffect(() => {
    if (selectedRuleId && testAmount > 0 && eligibleEmployees.length > 0) {
      runCalculation();
    } else {
      setCalculatedDistribution([]);
      setEmployeeAmounts({});
    }
  }, [
    selectedRuleId,
    selectedRecordId,
    testAmount,
    eligibleEmployees.length,
    labDate,
  ]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const calculateLocalDistribution = () => {
    // Local fallback calculation matching backend enum: Equal=0, RoleScore=1, ByDepartment=2, WeightedHours=3
    const amounts = {};
    const ruleType = selectedRule?.type;

    if (ruleType === "Equal" || ruleType === 0) {
      // Equal split
      const equalAmount =
        Math.round((testAmount / eligibleEmployees.length) * 100) / 100;
      let remaining = testAmount;
      eligibleEmployees.forEach((emp, index) => {
        if (index === eligibleEmployees.length - 1) {
          amounts[emp.id] = remaining;
        } else {
          amounts[emp.id] = equalAmount;
          remaining -= equalAmount;
        }
      });
    } else if (
      ruleType === "RoleScore" ||
      ruleType === "ByJobRole" ||
      ruleType === "ByJobRoleScore" ||
      ruleType === 1
    ) {
      // RoleScore: split by job role percentage allocations
      if (!selectedRule.allocations || selectedRule.allocations.length === 0) {
        showNotification("Rule has no allocations defined", "error");
        return;
      }
      const employeesByRole = {};
      eligibleEmployees.forEach((emp) => {
        const roleId = emp.primaryJobRoleId || emp.jobRoleId;
        if (!roleId) return;
        if (!employeesByRole[roleId]) employeesByRole[roleId] = [];
        employeesByRole[roleId].push(emp);
      });
      const roleAllocs = selectedRule.allocations;
      const totalRoleMult =
        roleAllocs.reduce((sum, a) => sum + (a.scoreMultiplier || 1), 0) || 1;
      selectedRule.allocations.forEach((allocation) => {
        const roleEmployees = employeesByRole[allocation.jobRoleId] || [];
        if (roleEmployees.length === 0) return;
        const roleAmount =
          Math.round(
            ((testAmount * (allocation.scoreMultiplier || 1)) / totalRoleMult) *
              100,
          ) / 100;
        const amountPerEmployee =
          Math.round((roleAmount / roleEmployees.length) * 100) / 100;
        let roleRemaining = roleAmount;
        roleEmployees.forEach((emp, index) => {
          if (index === roleEmployees.length - 1) {
            amounts[emp.id] = roleRemaining;
          } else {
            amounts[emp.id] = amountPerEmployee;
            roleRemaining -= amountPerEmployee;
          }
        });
      });
    } else if (ruleType === "ByDepartment" || ruleType === 2) {
      // ByDepartment: split by department percentage allocations
      if (!selectedRule.allocations || selectedRule.allocations.length === 0) {
        showNotification("Rule has no allocations defined", "error");
        return;
      }
      const employeesByDept = {};
      eligibleEmployees.forEach((emp) => {
        const dept = emp.department || "FOH";
        if (!employeesByDept[dept]) employeesByDept[dept] = [];
        employeesByDept[dept].push(emp);
      });
      const deptAllocs = selectedRule.allocations;
      const totalDeptMult =
        deptAllocs.reduce((sum, a) => sum + (a.scoreMultiplier || 1), 0) || 1;
      selectedRule.allocations.forEach((allocation) => {
        if (!allocation.department) return;
        const deptEmployees = employeesByDept[allocation.department] || [];
        if (deptEmployees.length === 0) return;
        const deptAmount =
          Math.round(
            ((testAmount * (allocation.scoreMultiplier || 1)) / totalDeptMult) *
              100,
          ) / 100;
        const amountPerEmployee =
          Math.round((deptAmount / deptEmployees.length) * 100) / 100;
        let deptRemaining = deptAmount;
        deptEmployees.forEach((emp, index) => {
          if (index === deptEmployees.length - 1) {
            amounts[emp.id] = deptRemaining;
          } else {
            amounts[emp.id] = amountPerEmployee;
            deptRemaining -= amountPerEmployee;
          }
        });
      });
    } else if (ruleType === "WeightedHours" || ruleType === 3) {
      // WeightedHours: Individual points = hours × role multiplier from rule allocations
      const roleMultiplierMap = {};
      (selectedRule.allocations || [])
        .filter((a) => a.jobRoleId && (a.scoreMultiplier || 0) > 0)
        .forEach((a) => {
          roleMultiplierMap[a.jobRoleId] = a.scoreMultiplier;
        });
      let totalPoints = 0;
      const employeePoints = {};
      eligibleEmployees.forEach((emp) => {
        const hours = emp.hoursWorked || 0;
        const roleId = emp.primaryJobRoleId || emp.jobRoleId;
        const multiplier =
          roleId && roleMultiplierMap[roleId] ? roleMultiplierMap[roleId] : 1.0;
        const points = hours * multiplier;
        employeePoints[emp.id] = points;
        totalPoints += points;
      });
      if (totalPoints === 0) {
        // Fall back to equal if no hours data
        const equalAmount =
          Math.round((testAmount / eligibleEmployees.length) * 100) / 100;
        let remaining = testAmount;
        eligibleEmployees.forEach((emp, index) => {
          if (index === eligibleEmployees.length - 1) {
            amounts[emp.id] = remaining;
          } else {
            amounts[emp.id] = equalAmount;
            remaining -= equalAmount;
          }
        });
      } else {
        let distributed = 0;
        eligibleEmployees.forEach((emp, index) => {
          const points = employeePoints[emp.id] || 0;
          let amount;
          if (index === eligibleEmployees.length - 1) {
            amount = testAmount - distributed;
          } else {
            amount =
              Math.round((points / totalPoints) * testAmount * 100) / 100;
            distributed += amount;
          }
          amounts[emp.id] = amount;
        });
      }
    }

    setEmployeeAmounts(amounts);
  };

  const runCalculation = async () => {
    if (!selectedRule || testAmount <= 0 || eligibleEmployees.length === 0) {
      setCalculatedDistribution([]);
      setEmployeeAmounts({});
      return;
    }
    setIsCalculating(true);
    try {
      // Pass labDate as periodStart/periodEnd so the backend uses clock records for the correct date
      const periodStart = labDate ? new Date(labDate).toISOString() : null;
      const periodEnd = labDate
        ? new Date(labDate + "T23:59:59").toISOString()
        : null;
      const result = await previewDistribution(
        selectedRule.id,
        testAmount,
        currentLocation?.id || null,
        periodStart,
        periodEnd,
      );
      if (result.success && result.data?.distributions) {
        // Filter distributions to only eligible employees
        const filteredDistributions = result.data.distributions.filter((d) =>
          eligibleEmployeeIds.has(d.employeeId),
        );
        setCalculatedDistribution(filteredDistributions);
        const amounts = {};
        filteredDistributions.forEach((d) => {
          amounts[d.employeeId] = d.amount;
        });
        setEmployeeAmounts(amounts);
      } else {
        showNotification(
          "Failed to calculate distribution: " +
            (result.message || "Unknown error"),
          "error",
        );
        calculateLocalDistribution();
      }
    } catch (err) {
      console.error("Preview failed:", err);
      showNotification(
        "Distribution preview failed. Using local calculation.",
        "warning",
      );
      calculateLocalDistribution();
    } finally {
      setIsCalculating(false);
    }
  };

  const strategyBreakdown = useMemo(() => {
    if (!selectedRule || testAmount === 0 || eligibleEmployees.length === 0)
      return null;
    const ruleType = selectedRule.type;

    if (ruleType === "Equal" || ruleType === 0) {
      const perEmployee = testAmount / eligibleEmployees.length;
      return {
        type: "equal",
        description: "Total TRONC divided equally among all eligible employees",
        formula: `${formatCurrency(testAmount)} / ${eligibleEmployees.length} employees`,
        result: formatCurrency(perEmployee),
        items: [
          {
            label: "Per Employee",
            value: formatCurrency(perEmployee),
            count: eligibleEmployees.length,
            percentage: 100,
          },
        ],
      };
    }

    if (
      ruleType === "RoleScore" ||
      ruleType === "ByJobRole" ||
      ruleType === "ByJobRoleScore" ||
      ruleType === 1
    ) {
      const totalRoleMult =
        (selectedRule.allocations || []).reduce(
          (sum, a) => sum + (a.scoreMultiplier || 1),
          0,
        ) || 1;
      const items = (selectedRule.allocations || []).map((allocation) => {
        const roleEmployees = eligibleEmployees.filter(
          (e) =>
            e.jobRoleId === allocation.jobRoleId ||
            e.primaryJobRoleId === allocation.jobRoleId,
        );
        const mult = allocation.scoreMultiplier || 1;
        const roleAmount = testAmount * (mult / totalRoleMult);
        const perEmployee =
          roleEmployees.length > 0 ? roleAmount / roleEmployees.length : 0;
        return {
          label: allocation.jobRoleName || "Unknown Role",
          percentage: (mult / totalRoleMult) * 100,
          value: formatCurrency(roleAmount),
          count: roleEmployees.length,
          perEmployee: formatCurrency(perEmployee),
        };
      });
      return {
        type: "byJobRole",
        description: "TRONC split by configured score multiplier per job role",
        formula:
          "(Role Multiplier ÷ Total Multipliers) × Total TRONC ÷ Employees in Role",
        items,
      };
    }

    if (ruleType === "ByDepartment" || ruleType === 2) {
      const totalDeptMult =
        (selectedRule.allocations || []).reduce(
          (sum, a) => sum + (a.scoreMultiplier || 1),
          0,
        ) || 1;
      const items = (selectedRule.allocations || []).map((allocation) => {
        const deptEmployees = eligibleEmployees.filter(
          (e) => e.department === allocation.department,
        );
        const mult = allocation.scoreMultiplier || 1;
        const deptAmount = testAmount * (mult / totalDeptMult);
        const perEmployee =
          deptEmployees.length > 0 ? deptAmount / deptEmployees.length : 0;
        return {
          label: allocation.department || "Unknown Department",
          percentage: (mult / totalDeptMult) * 100,
          value: formatCurrency(deptAmount),
          count: deptEmployees.length,
          perEmployee: formatCurrency(perEmployee),
        };
      });
      return {
        type: "byDepartment",
        description:
          "TRONC split by configured score multiplier per department",
        formula:
          "(Dept Multiplier ÷ Total Multipliers) × Total TRONC ÷ Employees in Dept",
        items,
      };
    }

    if (ruleType === "WeightedHours" || ruleType === 3) {
      const roleMultMap = {};
      (selectedRule.allocations || [])
        .filter((a) => a.jobRoleId && (a.scoreMultiplier || 0) > 0)
        .forEach((a) => {
          roleMultMap[a.jobRoleId] = a.scoreMultiplier;
        });
      const employeeWeights = eligibleEmployees.map((emp) => {
        const hours = emp.hoursWorked || 0;
        const roleId = emp.primaryJobRoleId || emp.jobRoleId;
        const multiplier =
          roleId && roleMultMap[roleId] ? roleMultMap[roleId] : 1.0;
        return { ...emp, weight: hours * multiplier };
      });
      const totalWeight = employeeWeights.reduce(
        (sum, emp) => sum + emp.weight,
        0,
      );
      const roleGroups = {};
      employeeWeights.forEach((emp) => {
        const roleName = emp.role || "Unassigned";
        if (!roleGroups[roleName])
          roleGroups[roleName] = { employees: [], totalWeight: 0 };
        roleGroups[roleName].employees.push(emp);
        roleGroups[roleName].totalWeight += emp.weight;
      });
      const items = Object.entries(roleGroups).map(([roleName, data]) => {
        const rolePercentage =
          totalWeight > 0 ? (data.totalWeight / totalWeight) * 100 : 0;
        const roleAmount = testAmount * (rolePercentage / 100);
        const perEmployee =
          data.employees.length > 0 ? roleAmount / data.employees.length : 0;
        return {
          label: roleName,
          percentage: Math.round(rolePercentage * 10) / 10,
          value: formatCurrency(roleAmount),
          count: data.employees.length,
          perEmployee: formatCurrency(perEmployee),
          weight: Math.round(data.totalWeight),
        };
      });
      return {
        type: "byWeightedHours",
        description:
          "Hours worked × role multiplier determines individual share",
        formula: "(Hours × Multiplier) / Total Points × Total TRONC",
        totalWeight: Math.round(totalWeight),
        items: items.sort((a, b) => b.percentage - a.percentage),
      };
    }

    return null;
  }, [selectedRule, testAmount, eligibleEmployees]);

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "Settings", path: "/settings/distribution-rules" },
    { label: "Distribution Lab", path: "/distribution-lab" },
  ];

  const getTotalDistributed = () =>
    Object.values(employeeAmounts).reduce((sum, amt) => sum + (amt || 0), 0);
  const getAverageAmount = () =>
    eligibleEmployees.length > 0
      ? getTotalDistributed() / eligibleEmployees.length
      : 0;
  const employeesWithAmounts = eligibleEmployees.map((emp) => ({
    ...emp,
    calculatedAmount: employeeAmounts[emp.id] || 0,
  }));

  const handleExport = () => {
    if (!selectedRule) {
      showNotification("Please select a rule first", "warning");
      return;
    }
    if (testAmount <= 0) {
      showNotification(
        "Please select a TRONC record or enter a test amount first",
        "warning",
      );
      return;
    }
    const headers = ["Employee", "Role", "Department", "Amount", "Percentage"];
    const rows = employeesWithAmounts.map((emp) => [
      emp.name,
      emp.role,
      emp.department || "N/A",
      formatCurrency(emp.calculatedAmount || 0),
      `${((emp.calculatedAmount / testAmount) * 100 || 0).toFixed(2)}%`,
    ]);
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const datePart = selectedRecord
      ? formatDate(selectedRecord.periodStart).replace(/\s+/g, "-")
      : "manual-test";
    a.download = `distribution-lab-${selectedRule.name.replace(/\s+/g, "-").toLowerCase()}-${datePart}-${labDate}.csv`;
    a.click();
    showNotification("Distribution exported to CSV");
  };

  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Distribution Lab - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Distribution Lab"
          description="Test distribution rules without affecting real data."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
              to test their distribution rules.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="ArrowLeft" size={16} />
              <span>Use the tenant selector in the sidebar</span>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  const typeInfo = selectedRule
    ? getDistributionTypeInfo(selectedRule.type)
    : null;

  return (
    <>
      <Helmet>
        <title>
          Distribution Lab - {selectedTenant?.name || "Rocket Tronc"}
        </title>
        <meta
          name="description"
          content="Test distribution rules without affecting real data"
        />
      </Helmet>
      <PageLayout
        title="Distribution Lab"
        description="Test distribution rules with real TRONC data. Select a date, rule, and amount to simulate how it would be distributed with the active eligibility criteria."
        breadcrumbs={breadcrumbs}
      >
        {notification && (
          <div
            className={`mb-6 alert ${notification.type === "error" ? "alert-error" : notification.type === "warning" ? "alert-warning" : "alert-success"}`}
          >
            <Icon
              name={
                notification.type === "error"
                  ? "AlertCircle"
                  : notification.type === "warning"
                    ? "AlertTriangle"
                    : "CheckCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-auto">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        <div className="mb-6 bg-warning/10 border border-warning/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon
              name="FlaskConical"
              size={24}
              className="text-warning shrink-0"
            />
            <div>
              <h3 className="font-semibold text-warning mb-1">
                Testing Environment
              </h3>
              <p className="text-sm text-muted-foreground">
                This is a testing sandbox. Select a date, rule, and TRONC record
                to see how the distribution would work with the active
                eligibility criteria. Results are simulations only.
              </p>
            </div>
          </div>
        </div>

        {loading && (
          <div className="card p-8 text-center mb-6">
            <div className="spinner mx-auto mb-4" />
            <p className="text-muted-foreground">Loading data...</p>
          </div>
        )}

        {!loading && (
          <>
            {/* Test Configuration */}
            <div className="mb-6 card p-6 border-2 border-primary/20 bg-primary/5">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Settings2" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Test Configuration</h3>
                  <p className="text-sm text-muted-foreground">
                    Select a date, rule, and TRONC record to simulate
                    distribution
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Date Selector */}
                <div>
                  <label className="label text-sm mb-1">
                    Distribution Date
                  </label>
                  <input
                    type="date"
                    value={labDate}
                    onChange={(e) => {
                      setLabDate(e.target.value);
                      setEmployeeAmounts({});
                      setCalculatedDistribution([]);
                    }}
                    className="input w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Eligibility criteria effective on this date will be applied
                  </p>
                </div>
                {/* Distribution Rule */}
                <div>
                  <label className="label text-sm mb-1">
                    Distribution Rule
                  </label>
                  <select
                    value={selectedRuleId || ""}
                    onChange={(e) => {
                      setSelectedRuleId(e.target.value || null);
                      setEmployeeAmounts({});
                      setCalculatedDistribution([]);
                    }}
                    className="input w-full"
                  >
                    <option value="">Select a rule...</option>
                    {rules?.map((rule) => (
                      <option key={rule.id} value={rule.id}>
                        {rule.name}{" "}
                        {rule.isDraft
                          ? "(Draft)"
                          : rule.isActive
                            ? "(Active)"
                            : ""}
                      </option>
                    ))}
                  </select>
                </div>
                {/* TRONC Record */}
                <div>
                  <label className="label text-sm mb-1">
                    TRONC Record (Last 30 Days)
                  </label>
                  <select
                    value={selectedRecordId || ""}
                    onChange={(e) => {
                      setSelectedRecordId(e.target.value || null);
                      setManualAmount("");
                      setEmployeeAmounts({});
                      setCalculatedDistribution([]);
                    }}
                    className="input w-full"
                  >
                    <option value="">
                      {last30DaysRecords.length === 0
                        ? "No records — enter amount below"
                        : "Select a TRONC record..."}
                    </option>
                    {last30DaysRecords.map((record) => (
                      <option key={record.id} value={record.id}>
                        {formatDate(record.periodStart)} -{" "}
                        {formatCurrency(record.amount)}
                        {record.locationName && ` (${record.locationName})`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Manual amount input */}
              {!selectedRecordId && (
                <div className="mt-4">
                  <label className="label text-sm mb-1">Test Amount (£)</label>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 flex-1 max-w-xs">
                      <span className="text-muted-foreground font-medium">
                        £
                      </span>
                      <input
                        type="number"
                        value={manualAmount}
                        onChange={(e) => {
                          setManualAmount(e.target.value);
                          setEmployeeAmounts({});
                          setCalculatedDistribution([]);
                        }}
                        className="input w-full"
                        placeholder="e.g. 1000"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Enter a test amount to preview how the rule would
                      distribute it
                    </p>
                  </div>
                </div>
              )}

              {(isCalculating || previewLoading) && (
                <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/20 flex items-center gap-3">
                  <div className="spinner spinner-sm" />
                  <span className="text-sm text-muted-foreground">
                    Calculating distribution preview...
                  </span>
                </div>
              )}
              {selectedRecord && (
                <div className="mt-4 p-3 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Calendar" size={14} />
                      <span>Selected Record:</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="text-muted-foreground">Date: </span>
                        <strong>
                          {formatDate(selectedRecord.periodStart)}
                        </strong>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Amount: </span>
                        <strong className="text-primary">
                          {formatCurrency(selectedRecord.amount)}
                        </strong>
                      </div>
                      {selectedRecord.cashTips > 0 && (
                        <div>
                          <span className="text-muted-foreground">Cash: </span>
                          <span>{formatCurrency(selectedRecord.cashTips)}</span>
                        </div>
                      )}
                      {selectedRecord.cardTips > 0 && (
                        <div>
                          <span className="text-muted-foreground">Card: </span>
                          <span>{formatCurrency(selectedRecord.cardTips)}</span>
                        </div>
                      )}
                      {selectedRecord.serviceCharge > 0 && (
                        <div>
                          <span className="text-muted-foreground">
                            Service:{" "}
                          </span>
                          <span>
                            {formatCurrency(selectedRecord.serviceCharge)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {currentLocation && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap gap-3 items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="MapPin" size={14} />
                    <span>
                      Testing with employees from:{" "}
                      <strong>{currentLocation.name}</strong>
                    </span>
                    <span className="text-xs">
                      ({eligibleEmployees.length} eligible employees)
                    </span>
                  </div>
                  {clockRecords && clockRecords.length > 0 ? (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="badge badge-success flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        <span>
                          Using Clock Records ({clockRecords.length} records)
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="badge badge-warning flex items-center gap-1">
                        <Icon name="AlertTriangle" size={12} />
                        <span>No Clock Records — Using Estimated Hours</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Eligibility Criteria Panel */}
            <div className="mb-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Active Eligibility Criteria */}
              <div className="xl:col-span-2">
                <div
                  className={`card border-2 ${eligibilityCriteria ? "border-success/30" : "border-warning/30"}`}
                >
                  <div
                    className={`card-header ${eligibilityCriteria ? "bg-success/5" : "bg-warning/5"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg ${eligibilityCriteria ? "bg-success/10" : "bg-warning/10"} flex items-center justify-center`}
                        >
                          <Icon
                            name={
                              eligibilityCriteria
                                ? "ShieldCheck"
                                : "ShieldAlert"
                            }
                            size={20}
                            className={
                              eligibilityCriteria
                                ? "text-success"
                                : "text-warning"
                            }
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            Eligibility Criteria
                            {eligibilityCriteria && (
                              <span className="badge badge-success text-xs">
                                v{eligibilityCriteria.version}
                              </span>
                            )}
                            {eligibilityLoading && (
                              <span className="spinner spinner-sm" />
                            )}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {eligibilityCriteria
                              ? `Effective since ${formatDate(eligibilityCriteria.effectiveFrom)} applied to distribution on ${formatDate(labDate)}`
                              : `No criteria found for ${formatDate(labDate)} using system defaults`}
                          </p>
                        </div>
                      </div>
                      {!eligibilityCriteria && !eligibilityLoading && (
                        <button
                          onClick={handleEnsureEligibility}
                          className="btn btn-warning btn-sm"
                        >
                          <Icon name="Plus" size={16} />
                          Create Default
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="card-content p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <EligibilityBadge
                        label="Exclude Probation"
                        enabled={eligibilityRules.excludeEmployeesInProbation}
                      />
                      <EligibilityBadge
                        label="Exclude Management"
                        enabled={eligibilityRules.excludeManagementRoles}
                      />
                      <EligibilityBadge
                        label="Min Service Days"
                        enabled={eligibilityRules.requireMinimumServiceDays}
                        value={
                          eligibilityRules.requireMinimumServiceDays
                            ? `${eligibilityRules.minimumServiceDays}d`
                            : null
                        }
                      />
                      <EligibilityBadge
                        label="Min Hours"
                        enabled={eligibilityRules.requireMinimumHoursInPeriod}
                        value={
                          eligibilityRules.requireMinimumHoursInPeriod
                            ? `${eligibilityRules.minimumHoursInPeriod}h/period`
                            : null
                        }
                      />
                      <EligibilityBadge
                        label="Part-Time"
                        enabled={eligibilityCriteria?.includePartTime ?? true}
                      />
                      <EligibilityBadge
                        label="Casual Staff"
                        enabled={eligibilityCriteria?.includeCasual ?? true}
                      />
                      <EligibilityBadge
                        label="Role Eligibility"
                        enabled={eligibilityRules.requireRoleIsEligible}
                      />
                      <EligibilityBadge
                        label="Active Role"
                        enabled={eligibilityRules.requireRoleIsActive}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Eligibility Summary */}
              <div className="xl:col-span-1">
                <div className="card border-2 border-border">
                  <div className="card-header">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="Users" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Employee Eligibility</h3>
                        <p className="text-xs text-muted-foreground">
                          Based on criteria for {formatDate(labDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="card-content p-4">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-lg font-bold">
                          {eligibilitySummary.totalEmployees}
                        </p>
                        <p className="text-xs text-muted-foreground">Total</p>
                      </div>
                      <div className="p-3 bg-success/5 rounded-lg border border-success/20">
                        <p className="text-lg font-bold text-success">
                          {eligibilitySummary.eligibleCount}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Eligible
                        </p>
                      </div>
                      <div className="p-3 bg-error/5 rounded-lg border border-error/20">
                        <p className="text-lg font-bold text-error">
                          {eligibilitySummary.ineligibleCount}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Excluded
                        </p>
                      </div>
                    </div>

                    {eligibilitySummary.ineligibleCount > 0 && (
                      <div className="mt-3 p-3 bg-muted/50 rounded-lg space-y-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          Exclusion Reasons
                        </p>
                        {Object.entries(eligibilitySummary.reasonCounts)
                          .sort((a, b) => b[1] - a[1])
                          .map(([code, count]) => (
                            <div
                              key={code}
                              className="flex items-center justify-between text-xs"
                            >
                              <span className="text-muted-foreground truncate mr-2">
                                {EligibilityReasonLabels[code] || code}
                              </span>
                              <span className="font-semibold text-error shrink-0">
                                {count}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Selected Rule & Strategy Breakdown */}
        {!loading && selectedRule && (
          <div className="card mb-6 border-2 border-primary/20 overflow-hidden">
            <div className="p-6 flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div
                  className={`w-14 h-14 rounded-xl ${typeInfo?.bgColor || "bg-primary/10"} flex items-center justify-center`}
                >
                  <Icon
                    name={typeInfo?.icon || "Settings"}
                    size={28}
                    className={typeInfo?.color || "text-primary"}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-xl font-heading font-bold">
                      {selectedRule.name}
                    </h2>
                    {selectedRule.isDraft && (
                      <span className="badge badge-warning">Draft</span>
                    )}
                    {selectedRule.isActive && !selectedRule.isDraft && (
                      <span className="badge badge-success">Active</span>
                    )}
                    {!selectedRule.isActive && !selectedRule.isDraft && (
                      <span className="badge badge-neutral">Inactive</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">{typeInfo?.name}</span>
                    {selectedRule.description && (
                      <span> &bull; {selectedRule.description}</span>
                    )}
                  </p>
                </div>
              </div>
              <Link
                to="/settings/distribution-rules"
                className="btn btn-outline btn-sm"
              >
                <Icon name="Settings" size={16} />
                Edit Rules
              </Link>
            </div>

            {strategyBreakdown && Object.keys(employeeAmounts).length > 0 && (
              <div className="border-t border-border p-6 bg-muted/30">
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon
                      name="Calculator"
                      size={18}
                      className="text-primary"
                    />
                    <h3 className="font-semibold">Distribution Strategy</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {strategyBreakdown.description}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 font-mono bg-muted px-2 py-1 rounded inline-block">
                    {strategyBreakdown.formula}
                  </p>
                </div>

                {strategyBreakdown.type === "equal" ? (
                  <div className="bg-card rounded-lg border border-border p-6 text-center">
                    <div className="text-4xl font-bold text-primary mb-2">
                      {strategyBreakdown.result}
                    </div>
                    <p className="text-muted-foreground">per employee</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {formatCurrency(testAmount)} / {eligibleEmployees.length}{" "}
                      = {strategyBreakdown.result}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide px-3 pb-2">
                      <div className="col-span-4">
                        {strategyBreakdown.type === "byDepartment"
                          ? "Department"
                          : "Role"}
                      </div>
                      <div className="col-span-2 text-right">Share</div>
                      <div className="col-span-2 text-right">Amount</div>
                      <div className="col-span-2 text-right">Staff</div>
                      <div className="col-span-2 text-right">Per Person</div>
                    </div>
                    {strategyBreakdown.items.map((item, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-12 gap-2 items-center p-3 bg-card rounded-lg border border-border hover:border-primary/30 transition-colors"
                      >
                        <div className="col-span-4 flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor: `hsl(${(index * 60) % 360}, 70%, 50%)`,
                            }}
                          />
                          <span className="font-medium truncate">
                            {item.label}
                          </span>
                        </div>
                        <div className="col-span-2 text-right">
                          <span className="font-bold text-primary">
                            {item.percentage}%
                          </span>
                        </div>
                        <div className="col-span-2 text-right">
                          <span className="font-semibold">{item.value}</span>
                        </div>
                        <div className="col-span-2 text-right text-muted-foreground">
                          {item.count}
                        </div>
                        <div className="col-span-2 text-right">
                          <span className="font-medium text-success">
                            {item.perEmployee}
                          </span>
                        </div>
                      </div>
                    ))}
                    <div className="grid grid-cols-12 gap-2 items-center p-3 bg-primary/5 rounded-lg border-2 border-primary/20 mt-2">
                      <div className="col-span-4 font-bold">Total</div>
                      <div className="col-span-2 text-right font-bold">
                        100%
                      </div>
                      <div className="col-span-2 text-right font-bold">
                        {formatCurrency(testAmount)}
                      </div>
                      <div className="col-span-2 text-right text-muted-foreground">
                        {eligibleEmployees.length}
                      </div>
                      <div className="col-span-2 text-right font-bold text-success">
                        {formatCurrency(getAverageAmount())}
                        <span className="text-xs text-muted-foreground ml-1">
                          avg
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {strategyBreakdown.type === "byWeightedHours" && (
                  <div className="mt-4 text-xs text-muted-foreground">
                    <span>
                      Total Weighted Points:{" "}
                      <strong>{strategyBreakdown.totalWeight}</strong> (hours ×
                      role multiplier)
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {!loading && !selectedRule && (
          <div className="card mb-6 p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-info/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="FlaskConical" size={32} className="text-info" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              Select a Rule to Test
            </h3>
            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
              Choose a distribution rule from the dropdown above to simulate how
              it would distribute TRONC among employees.
            </p>
            <Link to="/settings/distribution-rules" className="btn btn-outline">
              <Icon name="Plus" size={18} />
              Create New Rule
            </Link>
          </div>
        )}

        {!loading &&
          selectedRule &&
          Object.keys(employeeAmounts).length > 0 && (
            <div className="space-y-6">
              <CalculationSummary
                totalAmount={testAmount}
                recipientCount={eligibleEmployees.length}
                averageAmount={getAverageAmount()}
                remainingBalance={testAmount - getTotalDistributed()}
              />
              <PageSection title="Simulated Employee Allocations">
                <EmployeeDistributionList
                  employees={employeesWithAmounts}
                  onAmountChange={() => {}}
                  onOverrideNotesChange={() => {}}
                  isProcessing={false}
                  totalAmount={testAmount}
                  readOnly={true}
                />
              </PageSection>
              <div className="card p-6 border-2 border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <Icon
                        name="Download"
                        size={20}
                        className="text-muted-foreground"
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold">Export Results</h3>
                      <p className="text-sm text-muted-foreground">
                        Download the simulated distribution as CSV
                      </p>
                    </div>
                  </div>
                  <button className="btn btn-outline" onClick={handleExport}>
                    <Icon name="Download" size={18} />
                    Export to CSV
                  </button>
                </div>
              </div>
            </div>
          )}

        {!loading && selectedRule && eligibleEmployees.length === 0 && (
          <div className="card p-8 text-center">
            <div className="empty-state-icon mx-auto mb-4">
              <Icon name="Users" size={32} />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              No eligible employees
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              No employees passed the eligibility criteria for{" "}
              {formatDate(labDate)}
              {currentLocation ? ` at ${currentLocation.name}` : ""}. Check the
              eligibility criteria panel above for details.
            </p>
          </div>
        )}
      </PageLayout>
    </>
  );
};

/** Small read-only badge for eligibility rule display */
const EligibilityBadge = ({ label, enabled, value }) => (
  <div
    className={`flex items-center gap-2 p-2 rounded-lg text-xs ${enabled ? "bg-success/10 text-success border border-success/20" : "bg-muted text-muted-foreground border border-border"}`}
  >
    <Icon name={enabled ? "Check" : "X"} size={12} />
    <span>
      {label}
      {value && <span className="font-semibold ml-1">{value}</span>}
    </span>
  </div>
);

export default DistributionLab;
